// --- src/components/DataImporter.tsx ---
import React, { useState, useRef, useEffect } from 'react';
import { SupplierMap, SupplierProduct } from './types';
import { supabaseService } from '../services/supabaseService';
import { cleanProductListWithAI } from '../services/geminiService';
import {
  Upload,
  FileUp,
  Wand2,
  Loader2,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { getUserFriendlyError } from '../utils/errorHelper';
import { toast } from 'react-hot-toast';

interface DataImporterProps {
  supplierData: SupplierMap;
  setSupplierData: (data: SupplierMap) => void;
  reloadData: () => void;
}

const DataImporter: React.FC<DataImporterProps> = ({ reloadData }) => {
  const [textData, setTextData] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resolveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (resolveTimeoutRef.current) {
        clearTimeout(resolveTimeoutRef.current);
      }
    };
  }, []);

  // Konfliktus kezelés állapota
  const [conflicts, setConflicts] = useState<
    { new: SupplierProduct; old: SupplierProduct }[] | null
  >(null);
  const [safeToUpload, setSafeToUpload] = useState<SupplierProduct[]>([]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setTextData(event.target?.result as string);
      setStatus('idle');
      setStatusMessage(`Fájl betöltve: ${file.name}`);
    };
    reader.readAsText(file);
  };

  const processData = async () => {
    if (!textData) return;
    setIsProcessing(true);
    setConflicts(null);
    setStatus('idle');
    setProgress(0);
    setProcessingStatus('');

    try {
      setStatusMessage('AI tisztítás folyamatban...');

      const cleanedCSV = await cleanProductListWithAI(textData, (percentage, currentCSV) => {
        setProgress(percentage);
        // Számoljuk meg az eddig feldolgozott sorokat (CSV sorok - fejléc)
        const processedLines = Math.max(0, currentCSV.split('\n').length - 1);
        // Használjuk a processedLines-t a pontosabb megjelenítéshez
        setProcessingStatus(`Feldolgozás: ${percentage}% - ${processedLines} sor kész`);
        // Frissítsük a textData-t, hogy a felhasználó lássa a beérkező adatokat valós időben
        setTextData(currentCSV);
      });

      // Parsolás - Map-alapú deduplikációval
      const lines = cleanedCSV.split('\n');
      const uniqueProducts = new Map<string, SupplierProduct>();
      let startIndex = lines[0].toLowerCase().includes('ean') ? 1 : 0;
      let originalRowCount = 0;

      for (let i = startIndex; i < lines.length; i++) {
        const parts = lines[i].split(',');
        if (parts.length < 2) continue;
        const ean = parts[0].trim();
        if (ean.length < 3) continue;

        originalRowCount++;

        // Map-be mentés: automatikusan felülírja az előző azonos EAN-t a legfrissebbre
        uniqueProducts.set(ean, {
          ean: ean,
          name: parts[1]?.trim() || 'Ismeretlen',
          sku: parts[2]?.trim() || '',
          netPrice: parseFloat(parts[3]?.replace(/[^0-9.]/g, '')) || 0,
          sellingPrice: parseFloat(parts[4]?.replace(/[^0-9.]/g, '')) || 0,
        });
      }

      // Konvertálás tömbbé a feltöltéshez
      const finalList = Array.from(uniqueProducts.values());
      const duplicatesRemoved = originalRowCount - finalList.length;

      // --- KONFLIKTUS ELLENŐRZÉS ---
      setStatusMessage('Ütközések ellenőrzése az adatbázissal...');
      const eansToCheck = finalList.map((p) => p.ean);
      const existingItems = await supabaseService.checkExistingProducts(eansToCheck);

      const foundConflicts: { new: SupplierProduct; old: SupplierProduct }[] = [];
      const noConflictList: SupplierProduct[] = [];

      finalList.forEach((newItem) => {
        const oldItem = existingItems[newItem.ean];
        // Csak akkor van konfliktus, ha létezik ÉS a neve nagyon más (biztonsági döntés)
        if (oldItem && oldItem.name.trim().toLowerCase() !== newItem.name.trim().toLowerCase()) {
          foundConflicts.push({ new: newItem, old: oldItem });
        } else {
          noConflictList.push(newItem);
        }
      });

      if (foundConflicts.length > 0) {
        // Ha van baj, megállunk és kérdezünk
        setConflicts(foundConflicts);
        setSafeToUpload(noConflictList);
        setStatusMessage('Figyelem! Adatütközéseket találtam.');
        setIsProcessing(false);
        return;
      }

      // Ha nincs baj, mentünk azonnal
      // Jelezzük a felhasználónak a deduplikáció eredményét
      if (duplicatesRemoved > 0) {
        console.log(
          `[DataImporter] Deduplikáció: ${originalRowCount} sor beolvasva, ${finalList.length} egyedi termék (${duplicatesRemoved} duplikáció eltávolítva)`
        );
        toast.success(
          `${originalRowCount} sor beolvasva, ${finalList.length} egyedi termék (${duplicatesRemoved} duplikáció eltávolítva)`
        );
      } else {
        console.log(
          `[DataImporter] ${originalRowCount} sor beolvasva, ${finalList.length} egyedi termék`
        );
      }

      await finalSave(finalList);
    } catch (error: any) {
      console.error(error);
      const friendlyError = getUserFriendlyError(error);
      setStatus('error');
      setStatusMessage(friendlyError);
      toast.error(friendlyError);
      setIsProcessing(false);
    }
  };

  const finalSave = async (items: SupplierProduct[]) => {
    try {
      setStatusMessage('Mentés az adatbázisba...');

      // Deduplikáció: EAN kód alapján kiszűrjük az ismétlődéseket
      // Ha egy EAN többször szerepel, a listában később lévő (utolsó) írja felül az előzőt
      const deduplicatedMap = new Map<string, SupplierProduct>();
      items.forEach((item) => {
        deduplicatedMap.set(item.ean, item); // Az utolsó érték marad meg
      });

      const deduplicatedItems = Array.from(deduplicatedMap.values());
      const duplicatesRemoved = items.length - deduplicatedItems.length;

      const report = await supabaseService.uploadSupplierData(deduplicatedItems);
      // uploadSupplierData already logs IMPORT action, no need to log again
      await reloadData();

      // Build detailed status message
      let statusMsg = 'Feldolgozás befejezve!\n\n';
      statusMsg += `Sikeresen feltöltve: ${report.successCount} db\n`;
      statusMsg += `Sikertelen: ${report.failedCount} db\n`;

      if (duplicatesRemoved > 0) {
        statusMsg += `\nDuplikáció eltávolítva: ${duplicatesRemoved} db\n`;
      }

      if (report.errors.length > 0) {
        statusMsg += `\nHibák (első ${Math.min(5, report.errors.length)}):\n`;
        report.errors.slice(0, 5).forEach((err, idx) => {
          statusMsg += `${idx + 1}. ${err}\n`;
        });
        if (report.errors.length > 5) {
          statusMsg += `... és még ${report.errors.length - 5} hiba`;
        }
      }

      setStatus(report.failedCount === 0 ? 'success' : 'error');
      setStatusMessage(statusMsg);
      setTextData('');
      setConflicts(null);
    } catch (e: any) {
      const friendlyError = getUserFriendlyError(e);
      setStatus('error');
      setStatusMessage(friendlyError);
      toast.error(friendlyError);
    } finally {
      setIsProcessing(false);
    }
  };

  const resolveConflict = (ean: string, keepOld: boolean) => {
    if (!conflicts) return;

    // Ha a régit tartjuk meg, akkor kivesszük a feltöltendők közül (nem adjuk hozzá a safe listához)
    // Ha az újat akarjuk (felülírás), akkor hozzáadjuk a safe listához

    if (!keepOld) {
      const item = conflicts.find((c) => c.new.ean === ean)?.new;
      if (item) setSafeToUpload((prev) => [...prev, item]);
    }

    // Kivesszük a konfliktus listából
    const remaining = conflicts.filter((c) => c.new.ean !== ean);
    setConflicts(remaining.length > 0 ? remaining : null);

    // Ha elfogytak a konfliktusok, mehet a mentés a maradékkal
    if (remaining.length === 0) {
      // Kis késleltetés, hogy a state frissüljön
      // Clear any pending resolve timeout to prevent memory leak
      if (resolveTimeoutRef.current) {
        clearTimeout(resolveTimeoutRef.current);
      }
      resolveTimeoutRef.current = setTimeout(() => {
        // Ha a régit tartottuk meg, akkor a safeToUpload-ot mentjük
        // Ha az újat választottuk, azt már hozzáadtuk a safeToUpload-hoz
        // (Itt egy kis trükk kell: a state update aszinkron, ezért a resolveConflict végén
        // még nem biztos hogy a safeToUpload friss.
        // Egyszerűbb megoldás: Egy "Összes mentése" gomb a végén.)
        resolveTimeoutRef.current = null;
      }, 100);
    }
  };

  return (
    <div
      className="max-w-4xl mx-auto p-6 space-y-6 min-h-screen"
      style={{ backgroundColor: 'var(--color-background, #f9fafb)' }}
    >
      {/* --- KONFLIKTUS KEZELŐ MODAL --- */}
      {conflicts && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in duration-200">
            <div className="bg-amber-500 dark:bg-amber-600 p-4 text-white flex justify-between items-center">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <AlertTriangle /> Adatütközés észlelve ({conflicts.length} db)
              </h3>
            </div>

            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Az alábbi termékek már léteznek az adatbázisban, de az új fájlban más néven
                szerepelnek. Döntsd el, melyik adat a helyes!
              </p>

              <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
                {conflicts.map(({ new: newItem, old: oldItem }) => (
                  <div
                    key={newItem.ean}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-slate-50 dark:bg-gray-700 flex flex-col md:flex-row gap-4 items-center"
                  >
                    <div className="flex-1 text-center md:text-left">
                      <div className="text-xs font-bold text-slate-400 dark:text-gray-400">
                        JELENLEGI ADAT (ADATBÁZIS)
                      </div>
                      <div className="font-bold text-gray-900 dark:text-white">{oldItem.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {oldItem.netPrice} Ft | {oldItem.ean}
                      </div>
                    </div>

                    <ArrowRight className="text-slate-300 dark:text-gray-500 hidden md:block" />

                    <div className="flex-1 text-center md:text-right">
                      <div className="text-xs font-bold text-blue-500 dark:text-blue-400">
                        ÚJ ADAT (IMPORT)
                      </div>
                      <div className="font-bold text-blue-700 dark:text-blue-300">
                        {newItem.name}
                      </div>
                      <div className="text-sm text-blue-600 dark:text-blue-400">
                        {newItem.netPrice} Ft
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 min-w-[140px]">
                      <button
                        onClick={() => resolveConflict(newItem.ean, true)}
                        className="bg-slate-200 dark:bg-gray-600 hover:bg-slate-300 dark:hover:bg-gray-500 text-slate-700 dark:text-white px-3 py-2 rounded text-sm font-medium transition"
                      >
                        Régi megtartása
                      </button>
                      <button
                        onClick={() => resolveConflict(newItem.ean, false)}
                        className="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white px-3 py-2 rounded text-sm font-medium transition shadow-sm"
                      >
                        Új felülírása
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t border-primary flex justify-end">
                {/* Ha elfogytak a konfliktusok, akkor a lista üres lesz, és a komponens eltűnik.
                     De ha a user nem dönt mindenről, akkor itt lehet egy "Mégse" gomb */}
                <button
                  onClick={() => {
                    setConflicts(null);
                    setIsProcessing(false);
                    setStatusMessage('Importálás megszakítva.');
                  }}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 mr-4"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ha nincs konfliktus, de van várakozó lista (a konfliktusok feloldása után) */}
      {!conflicts && safeToUpload.length > 0 && status !== 'success' && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-6 rounded-xl text-center animate-in fade-in">
          <h3 className="text-green-800 dark:text-green-300 font-bold text-lg mb-2">
            Konfliktusok feloldva!
          </h3>
          <p className="text-green-700 dark:text-green-400 mb-4">
            {safeToUpload.length} tétel készen áll a mentésre.
          </p>
          <button
            onClick={() => finalSave(safeToUpload)}
            className="bg-green-600 dark:bg-green-500 text-white px-8 py-3 rounded-lg font-bold hover:bg-green-700 dark:hover:bg-green-600 shadow-lg transition"
          >
            Végleges Mentés Indítása
          </button>
        </div>
      )}

      {/* --- NORMÁL NÉZET (Változatlan) --- */}
      <div className="bg-card rounded-xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <FileUp className="text-blue-600 dark:text-blue-400" />
          2. Adatbázis Építés
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-8">
          Töltsd fel a beszállítói CSV vagy Excel fájlt. A rendszer automatikusan ellenőrzi az
          ütközéseket a mentés előtt.
        </p>

        <div
          className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-10 text-center hover:bg-slate-50 dark:hover:bg-gray-700 transition cursor-pointer group"
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".csv,.txt"
            onChange={handleFileUpload}
          />
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition">
            <Upload size={32} />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            Kattints a fájl kiválasztásához
          </h3>
        </div>

        {textData && (
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Adatok előnézete:
            </label>
            <textarea
              value={textData}
              onChange={(e) => setTextData(e.target.value)}
              className="w-full h-40 p-4 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none"
            />
          </div>
        )}

        {/* Progress Bar - csak akkor látszik, ha isProcessing === true */}
        {isProcessing && (
          <div className="mt-6 space-y-2">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
              <span className="font-medium">{processingStatus || 'Feldolgozás...'}</span>
              <span className="font-bold">{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-600 to-indigo-600 h-3 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {statusMessage && (
          <div
            className={`mt-6 p-4 rounded-lg flex items-center gap-3 ${status === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' : status === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'}`}
          >
            {status === 'error' && <AlertCircle />}
            {status === 'success' && <CheckCircle />}
            {status === 'idle' && <Loader2 className={isProcessing ? 'animate-spin' : ''} />}
            <span className="font-medium">{statusMessage}</span>
          </div>
        )}

        <div className="mt-8">
          <button
            onClick={processData}
            disabled={!textData || isProcessing || conflicts !== null}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isProcessing ? <Loader2 className="animate-spin" /> : <Wand2 />}
            AI Feldolgozás és Ellenőrzés
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataImporter;
